import type { InventoryInformationChange, PricingChange, PricingValues } from "@slfinrtl/ui-bff-routes"
import { FullServiceHistoryOptions } from "@slfinrtl/ui-bff-routes"
import { isEmptyIgnoring } from "src/common/isEmptyIgnoring"
import { removeUndefinedKeys } from "src/common/removeUndefinedKeys"
import type { InventoryInformationFormValues } from "../InventoryInformationFormValues"

export const mapToInventoryInformationChange = (
	values: Partial<InventoryInformationFormValues> & {
		pricingUseCase: PricingValues["pricingUseCase"]
	},
): InventoryInformationChange | undefined => {
	const result: InventoryInformationChange = {
		conditionAndService: {
			accidentDescription: values.accidentDescription,
			accidentFree: values.accidentFree,
			actualReconditioningCosts: values.reconditioningAmount,
			approximateReconditioningCosts: values.reconditioningApproximateAmount,
			batteryCapacityDate: values.batteryCapacityDate,
			batteryCapacityMileage: values.batteryCapacityMileage,
			batteryCapacityPercentage: values.batteryCapacityPercentage,
			carPassUrl: values.carPassUrl,
			firstRegistrationDate: values.firstRegistrationDate,
			formerRentalCar: values.formerRentalCar,
			fullServiceHistory:
				values.fullServiceHistory === FullServiceHistoryOptions.COMPLETE
					? true
					: values.fullServiceHistory === FullServiceHistoryOptions.INCOMPLETE
						? false
						: undefined,
			fullServiceHistoryInPorscheCenter: values.fullServiceHistoryInPorscheCenter,
			latestMaintenanceDate: values.latestMaintenanceDate,
			latestMaintenanceMileage: values.latestMaintenanceMileage,
			latestMaintenanceScope: values.latestMaintenanceScope,
			latestMaintenanceWorkshop: values.latestMaintenanceWorkshop,
			mileage: values.mileage,
			nextGeneralInspectionDate: values.nextGeneralInspectionDate,
			nonSmoker: values.nonSmoker,
			previousDamage: values.previousDamage,
			previousDamageAmount: values.previousDamageAmount,
			previousDamageDescription: values.previousDamageDescription,
			previousOwners:
				values.previousOwners !== undefined && values.previousOwners !== null
					? Number.parseInt(values.previousOwners)
					: values.previousOwners,
			previousOwnerUsageHistory: values.previousOwnerUsageHistory,
			reconditioned: values.reconditioned,
			renewGeneralInspectionOnSale: values.renewGeneralInspectionOnSale,
		},
		inventoryAndIntake: {
			arrivalDate: values.arrivalDate,
			availableForCustomerFromDate: values.availableForCustomerFromDate,
			availableFromDate: values.availableFromDate,
			dataSource: values.dataSource,
			expectedArrivalDate: values.expectedArrivalDate,
			inventoryNumber: values.inventoryNumber,
			licensePlate: values.licensePlate,
			masterNumberCh: values.masterNumberCh,
			mileageAccordingToPreviousOwner: values.mileageAccordingToPreviousOwner,
			stockStatus: values.stockStatus,
			typeKeyNumber: values.typeKeyNumber,
			vehicleOrigin: values.vehicleOrigin,
			vehicleType: values.vehicleType,
		},
	}

	const listingChangeBase: Partial<PricingChange> = {
		minimumPrice: values.minimumPrice,
		purchasePrice: values.purchasePrice,
	}

	switch (values.pricingUseCase) {
		case "BASIC":
			result.pricing = {
				...listingChangeBase,
				price: values.price,
				pricingUseCase: "BASIC",
				vatDeductible: values.vatDeductible,
			}
			break
		case "ALWAYS_VAT_DEDUCTIBLE_BASIC":
			result.pricing = {
				...listingChangeBase,
				price: values.price,
				pricingUseCase: "ALWAYS_VAT_DEDUCTIBLE_BASIC",
				vatDeductible: values.vatDeductible,
			}
			break
		case "AUSTRALIA_NET_PRICE":
			result.pricing = {
				...listingChangeBase,
				netPrice: values.netPrice,
				price: values.price,
				pricingUseCase: "AUSTRALIA_NET_PRICE",
				vatDeductible: values.vatDeductible,
			}
			break
		case "CANADA_PRICE_BREAKDOWN":
			result.pricing = {
				...listingChangeBase,
				accessoryIds: values.accessoryIds,
				hasSpareWheel: values.hasSpareWheel,
				pricingUseCase: "CANADA_PRICE_BREAKDOWN",
				showLuxuryTax: values.showLuxuryTax,
				vehiclePrice: values.vehiclePrice,
			}
			break
		case "JAPAN_VEHICLE_AND_MISC":
			result.pricing = {
				...listingChangeBase,
				automobileTax: values.automobileTax,
				inspectionRegistration: values.inspectionRegistration,
				inspectionRegistrationAgencyFee: values.inspectionRegistrationAgencyFee,
				liabilityInsurance: values.liabilityInsurance,
				lightVehicleTaxEnvironmentalPerformanceLevy: values.lightVehicleTaxEnvironmentalPerformanceLevy,
				parkingCertificate: values.parkingCertificate,
				parkingCertificationAcquisitionAgencyFee: values.parkingCertificationAcquisitionAgencyFee,
				pricingUseCase: "JAPAN_VEHICLE_AND_MISC",
				recyclingFee: values.recyclingFee,
				tonnageTax: values.tonnageTax,
				vatDeductible: values.vatDeductible,
				vehiclePrice: values.vehiclePrice,
			}
			break
		case "EXTRA_COSTS_PRICE_BREAKDOWN":
			result.pricing = {
				...listingChangeBase,
				extraCostsVehicleType: values.extraCostsVehicleType,
				pricingUseCase: "EXTRA_COSTS_PRICE_BREAKDOWN",
				selectedExtraCostIds: values.selectedExtraCostIds,
				vatDeductible: values.vatDeductible,
				vehiclePrice: values.vehiclePrice,
			}
			break
	}

	// In case that all fields from values are undefined except pricingUseCase we have to return undefined for listing
	// This means that none of the fields from listing section changed, pricingUseCase is not optional in InventoryInformationChange
	if (isEmptyIgnoring(result.pricing, "pricingUseCase")) {
		result.pricing = undefined
	}

	return removeUndefinedKeys<InventoryInformationChange>(result)
}
